import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Button, 
  Space, 
  Progress, 
  List,
  message,
  Divider,
  Statistic,
  Row,
  Col,
  Descriptions,
  Tag,
  Collapse
} from 'antd'
import { 
  PlayCircleOutlined, 
  ReloadOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  BookOutlined,
  DatabaseOutlined
} from '@ant-design/icons'
import { apiRequest } from '../../utils/api'

const { Panel } = Collapse

function PageGeneration() {
  const [generationStatus, setGenerationStatus] = useState('idle') // idle, running, completed, error
  const [progress, setProgress] = useState(0)
  const [stats, setStats] = useState({
    totalBooks: 0,
    generatedBooks: 0,
    totalPages: 0,
    generatedPages: 0
  })
  const [recentTasks, setRecentTasks] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  
  // 获取生成统计信息
  useEffect(() => {
    fetchStats()
    fetchRecentTasks()
  }, [])
  
  const fetchStats = async () => {
    try {
      const response = await apiRequest('/admin/pages/stats')
      const data = await response.json()
      
      if (response.ok) {
        setStats(data.stats || {
          totalBooks: 0,
          generatedBooks: 0,
          totalPages: 0,
          generatedPages: 0
        })
      }
    } catch (error) {
      console.error('获取统计信息失败:', error)
    }
  }
  
  const fetchRecentTasks = async () => {
    try {
      const response = await apiRequest('/admin/pages/tasks')
      const data = await response.json()
      
      if (response.ok) {
        setRecentTasks(data.tasks || [])
      }
    } catch (error) {
      console.error('获取任务列表失败:', error)
    }
  }
  
  const handleGenerateAll = async () => {
    try {
      setIsGenerating(true)
      setGenerationStatus('running')
      setProgress(0)
      
      const response = await apiRequest('/admin/pages/generate', {
        method: 'POST',
        body: JSON.stringify({ type: 'all' })
      })
      
      if (response.ok) {
        message.success('页面生成任务已启动')
        // 轮询进度
        pollGenerationProgress()
      } else {
        const data = await response.json()
        message.error(data.error || '页面生成任务启动失败')
        setGenerationStatus('error')
      }
    } catch (error) {
      console.error('启动页面生成失败:', error)
      message.error('启动页面生成失败')
      setGenerationStatus('error')
    } finally {
      setIsGenerating(false)
    }
  }
  
  const handleRegenerateAll = async () => {
    try {
      setIsGenerating(true)
      setGenerationStatus('running')
      setProgress(0)
      
      const response = await apiRequest('/admin/pages/generate', {
        method: 'POST',
        body: JSON.stringify({ type: 'regenerate' })
      })
      
      if (response.ok) {
        message.success('页面重新生成任务已启动')
        // 轮询进度
        pollGenerationProgress()
      } else {
        const data = await response.json()
        message.error(data.error || '页面重新生成任务启动失败')
        setGenerationStatus('error')
      }
    } catch (error) {
      console.error('启动页面重新生成失败:', error)
      message.error('启动页面重新生成失败')
      setGenerationStatus('error')
    } finally {
      setIsGenerating(false)
    }
  }
  
  const pollGenerationProgress = () => {
    const interval = setInterval(async () => {
      try {
        const response = await apiRequest('/admin/pages/progress')
        const data = await response.json()
        
        if (response.ok) {
          setProgress(data.progress || 0)
          setStats(prev => ({
            ...prev,
            generatedBooks: data.generatedBooks || 0,
            generatedPages: data.generatedPages || 0
          }))
          
          if (data.status === 'completed') {
            clearInterval(interval)
            setGenerationStatus('completed')
            message.success('页面生成完成')
            fetchStats()
            fetchRecentTasks()
          } else if (data.status === 'error') {
            clearInterval(interval)
            setGenerationStatus('error')
            message.error('页面生成出错')
          }
        }
      } catch (error) {
        console.error('获取进度失败:', error)
        clearInterval(interval)
      }
    }, 2000)
  }
  
  const getStatusTag = (status) => {
    switch (status) {
      case 'completed':
        return <Tag icon={<CheckCircleOutlined />} color="success">已完成</Tag>
      case 'running':
        return <Tag icon={<ClockCircleOutlined />} color="processing">进行中</Tag>
      case 'error':
        return <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
      default:
        return <Tag icon={<ClockCircleOutlined />} color="default">待处理</Tag>
    }
  }
  
  const getGenerationStatusText = () => {
    switch (generationStatus) {
      case 'running':
        return '页面生成中...'
      case 'completed':
        return '页面生成完成'
      case 'error':
        return '页面生成出错'
      default:
        return '准备生成页面'
    }
  }
  
  const getGenerationStatusIcon = () => {
    switch (generationStatus) {
      case 'running':
        return <ReloadOutlined spin style={{ fontSize: 24, color: '#1890ff' }} />
      case 'completed':
        return <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
      case 'error':
        return <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
      default:
        return <FileTextOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
    }
  }
  
  return (
    <div className="page-generation">
      <Card className="management-card">
        <div className="generation-header">
          <Space size="large">
            <div className="status-icon">
              {getGenerationStatusIcon()}
            </div>
            <div>
              <h2>{getGenerationStatusText()}</h2>
              <p>生成静态页面以供访问</p>
            </div>
          </Space>
        </div>
        
        <Divider />
        
        {/* 统计信息 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic 
              title="总书籍数" 
              value={stats.totalBooks} 
              prefix={<BookOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="已生成书籍" 
              value={stats.generatedBooks} 
              prefix={<CheckCircleOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="总页数" 
              value={stats.totalPages} 
              prefix={<FileTextOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="已生成页面" 
              value={stats.generatedPages} 
              prefix={<DatabaseOutlined />} 
            />
          </Col>
        </Row>
        
        {/* 进度条 */}
        <div style={{ marginBottom: 24 }}>
          <Progress 
            percent={progress} 
            status={generationStatus === 'error' ? 'exception' : 'normal'} 
          />
        </div>
        
        {/* 操作按钮 */}
        <Space style={{ marginBottom: 24 }}>
          <Button 
            type="primary" 
            icon={<PlayCircleOutlined />}
            onClick={handleGenerateAll}
            loading={isGenerating}
            disabled={generationStatus === 'running'}
          >
            生成所有页面
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={handleRegenerateAll}
            loading={isGenerating}
            disabled={generationStatus === 'running'}
          >
            重新生成所有页面
          </Button>
          <Button onClick={fetchStats}>
            刷新统计
          </Button>
        </Space>
        
        <Divider />
        
        {/* 最近任务 */}
        <Collapse defaultActiveKey={['1']} style={{ marginBottom: 24 }}>
          <Panel header="最近生成任务" key="1">
            <List
              dataSource={recentTasks}
              renderItem={task => (
                <List.Item>
                  <List.Item.Meta
                    title={task.book_title || '全部书籍'}
                    description={
                      <div>
                        <div>开始时间: {new Date(task.started_at).toLocaleString()}</div>
                        <div>结束时间: {task.completed_at ? new Date(task.completed_at).toLocaleString() : '进行中'}</div>
                      </div>
                    }
                  />
                  <div>
                    {getStatusTag(task.status)}
                  </div>
                </List.Item>
              )}
            />
          </Panel>
        </Collapse>
        
        {/* 说明信息 */}
        <Card size="small">
          <Descriptions title="页面生成说明" column={1} size="small">
            <Descriptions.Item label="生成方式">
              静态页面生成，存储在Cloudflare R2中
            </Descriptions.Item>
            <Descriptions.Item label="分页规则">
              按章节或固定字数分页（在书籍管理中设置）
            </Descriptions.Item>
            <Descriptions.Item label="访问路径">
              /books/{'{book_id}'}/{'{page_number}'}.html
            </Descriptions.Item>
            <Descriptions.Item label="注意事项">
              生成过程可能需要较长时间，请耐心等待
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Card>
    </div>
  )
}

export default PageGeneration